import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Loader2, Scissors, Play, Pause, Copy, Check, ExternalLink, Trash2 } from 'lucide-react';

interface LibraryClip {
  id: string;
  url: string;
  thumbnailUrl: string;
  filename: string;
  duration: number;
  width: number;
  height: number;
  aspectRatio: number;
  tags: string[];
  addedAt: string;
  source: string;
  sizeBytes: number;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 10);
  return `${m}:${s.toString().padStart(2, '0')}.${ms}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ClipperPanel() {
  // URL + loading
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Video state
  const [videoId, setVideoId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [videoWidth, setVideoWidth] = useState(0);
  const [videoHeight, setVideoHeight] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // In/out points
  const [inPoint, setInPoint] = useState(0);
  const [outPoint, setOutPoint] = useState(0);

  // Clip form
  const [clipName, setClipName] = useState('');
  const [tags, setTags] = useState('');
  const [clipping, setClipping] = useState(false);
  const [clipStatus, setClipStatus] = useState<string | null>(null);

  // Library
  const [clips, setClips] = useState<LibraryClip[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<'in' | 'out' | null>(null);

  // Load library on mount
  useEffect(() => {
    fetch('/api/library')
      .then(r => r.json())
      .then(data => { setClips(data.clips || []); setLoadingLibrary(false); })
      .catch(() => setLoadingLibrary(false));
  }, []);

  // Video time sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setCurrentTime(video.currentTime);
    const onEnd = () => setIsPlaying(false);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnd);
    return () => { video.removeEventListener('timeupdate', onTime); video.removeEventListener('ended', onEnd); };
  }, [previewUrl]);

  // Play/pause sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !previewUrl) return;
    if (isPlaying) video.play().catch(() => {});
    else video.pause();
  }, [isPlaying, previewUrl]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'i' || e.key === 'I') { setInPoint(currentTime); e.preventDefault(); }
      if (e.key === 'o' || e.key === 'O') { setOutPoint(currentTime); e.preventDefault(); }
      if (e.key === ' ') { setIsPlaying(p => !p); e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [currentTime]);

  // Drag handling for in/out markers
  const getTimeFromMouse = useCallback((clientX: number) => {
    if (!timelineRef.current || duration <= 0) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * duration;
  }, [duration]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const t = getTimeFromMouse(e.clientX);
      if (dragging === 'in') setInPoint(Math.min(t, outPoint - 0.1));
      else setOutPoint(Math.max(t, inPoint + 0.1));
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging, inPoint, outPoint, getTimeFromMouse]);

  // Load video
  const handleLoad = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError(null);
    setVideoId(null);
    setPreviewUrl(null);

    try {
      const res = await fetch('/api/video/load', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Load failed');

      setVideoId(data.videoId);
      setPreviewUrl(data.previewUrl);
      setDuration(data.duration);
      setVideoWidth(data.width);
      setVideoHeight(data.height);
      setInPoint(0);
      setOutPoint(data.duration);
      setClipName('');
      setTags('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  };

  // Clip video
  const handleClip = async () => {
    if (!videoId) return;
    setClipping(true);
    setClipStatus('Trimming & uploading...');

    try {
      const res = await fetch('/api/video/clip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoId,
          start: inPoint,
          end: outPoint,
          name: clipName || undefined,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          source: url,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Clip failed');

      setClips(prev => [data.clip, ...prev]);
      setClipStatus(`Saved! ${data.layoutSuggestion}`);
      setTimeout(() => setClipStatus(null), 4000);
    } catch (e: unknown) {
      setClipStatus(null);
      setError(e instanceof Error ? e.message : 'Clip failed');
    } finally {
      setClipping(false);
    }
  };

  // Seek on timeline click
  const handleTimelineClick = (e: React.MouseEvent) => {
    const t = getTimeFromMouse(e.clientX);
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      setCurrentTime(t);
    }
  };

  // Copy URL
  const handleCopy = (clip: LibraryClip) => {
    navigator.clipboard.writeText(clip.url);
    setCopiedId(clip.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const clipDuration = outPoint - inPoint;

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      {/* URL Input */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Link className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLoad()}
              placeholder="Paste YouTube, Instagram, or MP4 URL..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
            />
          </div>
          <button
            onClick={handleLoad}
            disabled={loading || !url.trim()}
            className="px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading...</> : 'Load'}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      {/* Video Player + Controls */}
      {previewUrl && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Video */}
          <div className="bg-black relative">
            <div className="max-w-2xl mx-auto">
              <video
                ref={videoRef}
                src={previewUrl}
                className="w-full"
                style={{ maxHeight: '400px' }}
                onClick={() => setIsPlaying(p => !p)}
              />
            </div>
            {/* Dimensions badge */}
            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {videoWidth}x{videoHeight}
            </div>
          </div>

          {/* Timeline */}
          <div className="px-4 pt-3 pb-2">
            <div
              ref={timelineRef}
              className="relative h-10 bg-gray-100 rounded-lg cursor-pointer select-none"
              onClick={handleTimelineClick}
            >
              {/* Selected region */}
              <div
                className="absolute top-0 bottom-0 bg-green-500/20 border-y-2 border-green-500/40"
                style={{
                  left: `${(inPoint / duration) * 100}%`,
                  width: `${(clipDuration / duration) * 100}%`,
                }}
              />

              {/* Playback position */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-gray-800 z-10 pointer-events-none"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />

              {/* In marker */}
              <div
                className="absolute top-0 bottom-0 w-2 bg-green-600 cursor-ew-resize z-20 rounded-l"
                style={{ left: `${(inPoint / duration) * 100}%`, transform: 'translateX(-100%)' }}
                onMouseDown={e => { e.stopPropagation(); setDragging('in'); }}
                title={`In: ${formatTime(inPoint)}`}
              >
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-green-700 font-medium whitespace-nowrap">
                  {formatTime(inPoint)}
                </div>
              </div>

              {/* Out marker */}
              <div
                className="absolute top-0 bottom-0 w-2 bg-red-500 cursor-ew-resize z-20 rounded-r"
                style={{ left: `${(outPoint / duration) * 100}%` }}
                onMouseDown={e => { e.stopPropagation(); setDragging('out'); }}
                title={`Out: ${formatTime(outPoint)}`}
              >
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-red-600 font-medium whitespace-nowrap">
                  {formatTime(outPoint)}
                </div>
              </div>
            </div>
          </div>

          {/* Controls row */}
          <div className="px-4 pb-3 flex items-center gap-3 flex-wrap">
            {/* Play/pause */}
            <button
              onClick={() => setIsPlaying(p => !p)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>

            {/* Current time */}
            <span className="text-xs text-gray-500 font-mono w-20">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <div className="w-px h-6 bg-gray-200" />

            {/* Set In/Out buttons */}
            <button
              onClick={() => setInPoint(currentTime)}
              className="px-2 py-1 text-xs border border-green-300 text-green-700 rounded hover:bg-green-50 font-medium"
            >
              Set In [I]
            </button>
            <button
              onClick={() => setOutPoint(currentTime)}
              className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50 font-medium"
            >
              Set Out [O]
            </button>

            {/* Selection duration */}
            <span className="text-xs text-gray-500">
              Selection: <span className="font-medium text-gray-800">{clipDuration.toFixed(1)}s</span>
            </span>

            <div className="flex-1" />

            {/* Clip form */}
            <input
              type="text"
              value={clipName}
              onChange={e => setClipName(e.target.value)}
              placeholder="Clip name..."
              className="w-32 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-green-500"
            />
            <input
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              placeholder="Tags (comma sep)"
              className="w-32 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-green-500"
            />

            <button
              onClick={handleClip}
              disabled={clipping || clipDuration <= 0}
              className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              {clipping ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Clipping...</>
              ) : (
                <><Scissors className="w-3 h-3" /> Clip & Save</>
              )}
            </button>
          </div>

          {/* Status message */}
          {clipStatus && (
            <div className="px-4 pb-3">
              <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                {clipStatus}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Library */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          Clip Library
          <span className="text-xs text-gray-400 font-normal">({clips.length} clips)</span>
        </h3>

        {loadingLibrary ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : clips.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No clips yet. Load a video above to get started.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {clips.map(clip => (
              <div key={clip.id} className="group rounded-lg border border-gray-200 overflow-hidden hover:border-green-300 transition-colors">
                {/* Thumbnail */}
                <div className="relative aspect-video bg-gray-900">
                  <img
                    src={clip.thumbnailUrl}
                    alt={clip.filename}
                    className="w-full h-full object-cover"
                  />
                  {/* Duration badge */}
                  <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                    {clip.duration.toFixed(1)}s
                  </div>
                  {/* Dimensions badge */}
                  <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                    {clip.width}x{clip.height}
                  </div>
                  {/* Play icon overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                      <Play className="w-4 h-4 text-gray-800 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-2">
                  <p className="text-xs font-medium text-gray-800 truncate" title={clip.id}>{clip.id}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {clip.tags?.slice(0, 2).map(tag => (
                      <span key={tag} className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{tag}</span>
                    ))}
                    <span className="text-[9px] text-gray-400 ml-auto">{formatFileSize(clip.sizeBytes)}</span>
                  </div>
                  {/* Actions */}
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={() => handleCopy(clip)}
                      className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1 text-[10px] border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      title="Copy URL"
                    >
                      {copiedId === clip.id ? <><Check className="w-2.5 h-2.5 text-green-600" /> Copied</> : <><Copy className="w-2.5 h-2.5" /> URL</>}
                    </button>
                    <a
                      href={clip.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-1.5 py-1 text-[10px] border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
